// src/components/BrowserInstructionModal.tsx
import React, { useState, useEffect, useRef } from 'react';

type InAppBrowserType = 'telegram' | 'whatsapp' | 'viber' | 'facebook' | 'instagram' | 'wechat' | 'other' | null;

const SITE_URL = 'https://ruyo-market.web.app';

export const BrowserInstructionModal: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [browserType, setBrowserType] = useState<InAppBrowserType>(null);
    const [isIOS, setIsIOS] = useState(false);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'fallback'>('idle');
    const linkInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const ua = navigator.userAgent;
        const uaLower = ua.toLowerCase();
        const referrer = (document.referrer || '').toLowerCase();

        const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
        const isAndroidDevice = /android/i.test(ua);
        const isMobile = isIOSDevice || isAndroidDevice;

        setIsIOS(isIOSDevice);

        // Десктоп — пропускаем
        if (!isMobile) return;

        const detectInAppBrowser = (): InAppBrowserType => {
            // --- Telegram ---
            // Telegram использует разные UA в зависимости от версии и платформы
            if (
                uaLower.includes('telegram') ||
                uaLower.includes('tgweb') ||
                // Telegram Android WebView — нет стандартного Chrome
                // Telegram iOS — нет Safari в UA, но есть специфичные маркеры
                referrer.includes('t.me') ||
                referrer.includes('telegram') ||
                // window.TelegramWebviewProxy существует в Telegram WebView
                typeof (window as any).TelegramWebviewProxy !== 'undefined' ||
                typeof (window as any).TelegramWebviewProxyProto !== 'undefined'
            ) {
                return 'telegram';
            }

            // --- WhatsApp ---
            if (uaLower.includes('whatsapp') || referrer.includes('whatsapp')) {
                return 'whatsapp';
            }

            // --- Viber ---
            if (uaLower.includes('viber') || referrer.includes('viber')) {
                return 'viber';
            }

            // --- Facebook / Messenger ---
            if (
                uaLower.includes('fban') ||
                uaLower.includes('fbav') ||
                uaLower.includes('fb_iab') ||
                uaLower.includes('fbios') ||
                uaLower.includes('messenger') ||
                referrer.includes('facebook.com') ||
                referrer.includes('messenger.com')
            ) {
                return 'facebook';
            }

            // --- Instagram ---
            if (uaLower.includes('instagram') || referrer.includes('instagram.com')) {
                return 'instagram';
            }

            // --- WeChat ---
            if (uaLower.includes('micromessenger') || referrer.includes('wechat')) {
                return 'wechat';
            }

            // --- Общий WebView ---
            // Android: настоящий Chrome содержит "Chrome/X" без "wv"
            // WebView содержит "wv" или "Version/X.X" без Chrome
            const isAndroidWebView =
                isAndroidDevice && (
                    uaLower.includes('; wv)') ||
                    uaLower.includes('webview') ||
                    // Нет "chrome" вообще — точно не браузер
                    (!uaLower.includes('chrome') && !uaLower.includes('firefox'))
                );

            // iOS: Safari содержит "Safari/" и "Version/"
            // iOS WebView — нет "Safari/" в UA
            const isIOSWebView =
                isIOSDevice && (
                    (!uaLower.includes('safari') && !uaLower.includes('crios') && !uaLower.includes('fxios')) ||
                    uaLower.includes('webview')
                );

            if (isAndroidWebView || isIOSWebView) {
                return 'other';
            }

            return null;
        };

        const detected = detectInAppBrowser();

        if (!detected) return;

        // Проверяем флаги показа
        try {
            if (localStorage.getItem('browser_instruction_never_show')) return;
        } catch (e) {
            // localStorage может быть недоступен
        }

        try {
            if (sessionStorage.getItem('browser_instruction_shown')) return;
        } catch (e) {
            // pass
        }

        setTimeout(() => {
            setIsVisible(true);
            setBrowserType(detected);
            try {
                sessionStorage.setItem('browser_instruction_shown', 'true');
            } catch (e) { /* pass */ }
        }, 1000);

    }, []);

    // Копирование — с фоллбэком через input + execCommand
    const copyLink = () => {
        const tryClipboardAPI = async () => {
            try {
                await navigator.clipboard.writeText(SITE_URL);
                setCopyStatus('copied');
                return true;
            } catch {
                return false;
            }
        };

        const tryExecCommand = () => {
            try {
                const input = linkInputRef.current;
                if (!input) return false;
                input.value = SITE_URL;
                input.style.display = 'block';
                input.focus();
                input.select();
                input.setSelectionRange(0, 99999); // для мобильных
                const success = document.execCommand('copy');
                input.style.display = 'none';
                if (success) {
                    setCopyStatus('copied');
                    return true;
                }
                return false;
            } catch {
                return false;
            }
        };

        tryClipboardAPI().then(success => {
            if (!success) {
                const fallbackSuccess = tryExecCommand();
                if (!fallbackSuccess) {
                    // Последний вариант — показываем ссылку, чтобы пользователь скопировал вручную
                    setCopyStatus('fallback');
                }
            }
        });
    };

    const getAppName = (): string => {
        switch (browserType) {
            case 'telegram': return 'Telegram';
            case 'whatsapp': return 'WhatsApp';
            case 'viber': return 'Viber';
            case 'facebook': return 'Facebook Messenger';
            case 'instagram': return 'Instagram';
            case 'wechat': return 'WeChat';
            default: return 'мессенджера';
        }
    };

    const handleNeverShow = () => {
        try { localStorage.setItem('browser_instruction_never_show', 'true'); } catch (e) { /* pass */ }
        setIsVisible(false);
    };

    if (!isVisible) return null;

    const appName = getAppName();

    return (
        <div className="browser-modal-overlay" onClick={() => setIsVisible(false)}>
            {/* Скрытый input для фоллбэка копирования */}
            <input
                ref={linkInputRef}
                readOnly
                defaultValue={SITE_URL}
                style={{
                    position: 'fixed',
                    top: '-9999px',
                    left: '-9999px',
                    display: 'none',
                    opacity: 0,
                }}
            />

            <div className="browser-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="browser-modal-close" onClick={() => setIsVisible(false)}>✕</button>

                <div className="browser-modal-icon">🌐</div>

                <h3>Откройте в браузере</h3>

                <div className="browser-modal-message">
                    Вы открыли сайт через <strong>{appName}</strong>.<br />
                    Для установки приложения откройте сайт в обычном браузере.
                </div>

                <div className="browser-modal-steps">
                    <div className="browser-modal-step">
                        <div className="step-number">1</div>
                        <div className="step-text">
                            Нажмите на меню (<strong>⋯</strong> или <strong>⋮</strong>) в углу экрана
                        </div>
                    </div>
                    <div className="browser-modal-step">
                        <div className="step-number">2</div>
                        <div className="step-text">
                            Выберите <strong>«Открыть в браузере»</strong> или <strong>«Chrome»</strong>
                        </div>
                    </div>
                    <div className="browser-modal-step">
                        <div className="step-number">3</div>
                        <div className="step-text">
                            Нажмите <strong>«Установить приложение»</strong> на баннере
                        </div>
                    </div>
                </div>

                <div className="browser-modal-divider"><span>или</span></div>

                {/* Состояния кнопки копирования */}
                {copyStatus === 'idle' && (
                    <button className="browser-modal-button copy-button" onClick={copyLink}>
                        📋 Скопировать ссылку
                    </button>
                )}

                {copyStatus === 'copied' && (
                    <div className="browser-modal-button copy-success">
                        ✅ Ссылка скопирована! Вставьте в браузер
                    </div>
                )}

                {/* Фоллбэк: показываем ссылку для ручного копирования */}
                {copyStatus === 'fallback' && (
                    <div className="browser-modal-fallback">
                        <p>Скопируйте ссылку вручную:</p>
                        <input
                            type="text"
                            readOnly
                            value={SITE_URL}
                            className="browser-modal-link-input"
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                        />
                    </div>
                )}

                <div className="browser-modal-note">
                    💡 Вставьте ссылку в адресную строку любого браузера
                </div>

                <button className="browser-modal-button secondary" onClick={handleNeverShow}>
                    Больше не показывать
                </button>
            </div>
        </div>
    );
};

export default BrowserInstructionModal;